import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";

/* ---------- Ledger Checker (standalone) ---------- */
/* Client-side mirror of backend apply_overrides() so per-row toggles
   recompute totals instantly without a roundtrip. */
export function applyLedgerOverrides(parsed, overrides) {
  if (!parsed) return parsed;
  const rows = parsed.rows || [];
  const totals = { eligible: 0, ineligible: 0, review: 0, stripped: 0, oncosts: 0, assessed: 0 };
  const counts = { eligible: 0, ineligible: 0, review: 0, stripped: 0, total: 0 };
  const newRows = rows.map(r => {
    const ov = overrides[r.row_id] || {};
    const verdict = ov.verdict;
    const oncostApplied = ov.oncost_applied;
    const reason = ov.reason || "";
    const toolElig = r.tool_eligibility || r.eligibility;
    let effElig;
    if (toolElig === "stripped") effElig = "stripped";
    else effElig = (verdict === "eligible" || verdict === "ineligible" || verdict === "review") ? verdict : toolElig;
    const toolOncost = Number(r.tool_oncost_aud || 0);
    const rate = r.oncost_pct_applied;
    const amount = Number(r.amount || 0);
    let effOncost, effOncostApplied;
    if (effElig !== "eligible") { effOncost = 0; effOncostApplied = false; }
    else if (oncostApplied === false) { effOncost = 0; effOncostApplied = false; }
    else if (oncostApplied === true) {
      effOncost = rate != null && rate > 0 ? Math.round(amount * Number(rate) / 100 * 100) / 100 : toolOncost;
      effOncostApplied = effOncost > 0;
    } else { effOncost = toolOncost; effOncostApplied = toolOncost > 0; }
    const assessed = effElig === "eligible" ? (amount + effOncost) : 0;
    const wasOverridden = !!(
      (verdict && verdict !== toolElig) ||
      (oncostApplied === true || oncostApplied === false) ||
      reason
    );
    counts.total += 1; counts[effElig] += 1;
    totals[effElig] += amount; totals.oncosts += effOncost; totals.assessed += assessed;
    return {
      ...r,
      eligibility: effElig,
      oncost_aud: effOncost,
      oncost_applied: effOncostApplied,
      assessed_aud: assessed,
      assessor_verdict: (verdict === "eligible" || verdict === "ineligible" || verdict === "review") ? verdict : null,
      assessor_oncost_applied: (oncostApplied === true || oncostApplied === false) ? oncostApplied : null,
      override_reason: reason,
      was_overridden: wasOverridden,
    };
  });
  Object.keys(totals).forEach(k => { totals[k] = Math.round(totals[k] * 100) / 100; });
  return { ...parsed, rows: newRows, totals, counts,
    overrides_applied: newRows.filter(r => r.was_overridden).length };
}


export function LedgerVerdictBtn({ row, verdict, label, klass, onChange, current }) {
  return (
    <button
      type="button"
      onClick={() => onChange({ verdict: current === verdict ? null : verdict })}
      data-testid={`ledger-row-${row.row_id}-${verdict}`}
      title={`Mark this line as ${label}`}
      className={`text-[10px] tracking-wider font-semibold px-1.5 py-0.5 rounded border ${
        current === verdict ? klass : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
      }`}
    >{label}</button>
  );
}


export function LedgerRowOverrideControls({ row, onChange, dense = false }) {
  // Stripped rows can't be overridden (they're submitted on-cost lines)
  const stripped = (row.tool_eligibility || row.eligibility) === "stripped";
  const verdict = row.assessor_verdict || null;
  const oncostApplied = row.assessor_oncost_applied;
  const reason = row.override_reason || "";

  if (stripped) {
    return (
      <span className="text-[9px] text-blue-700 italic" title="Submitted on-cost lines are stripped — they cannot be re-classified, only the assessor's recalculated on-cost on labour lines applies.">
        (stripped — locked)
      </span>
    );
  }
  const isLabour = row.category === "labour";
  return (
    <div className={`flex ${dense ? "gap-0.5" : "gap-1"} items-center flex-wrap`}>
      <LedgerVerdictBtn row={row} verdict="eligible" label="ELG" klass="bg-emerald-600 text-white border-emerald-600" onChange={onChange} current={verdict}/>
      <LedgerVerdictBtn row={row} verdict="ineligible" label="INEL" klass="bg-red-600 text-white border-red-600" onChange={onChange} current={verdict}/>
      <LedgerVerdictBtn row={row} verdict="review" label="REV" klass="bg-amber-500 text-white border-amber-500" onChange={onChange} current={verdict}/>
      {isLabour && (
        <button
          type="button"
          onClick={() => {
            const next = oncostApplied === true ? false : (oncostApplied === false ? null : true);
            onChange({ oncost_applied: next });
          }}
          data-testid={`ledger-row-${row.row_id}-oncost-toggle`}
          title="Toggle assessor on-cost application. Tri-state: tool default → ON → OFF → tool default."
          className={`text-[10px] tracking-wider font-semibold px-1.5 py-0.5 rounded border ${
            oncostApplied === true ? "bg-blue-600 text-white border-blue-600"
              : oncostApplied === false ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
          }`}
        >
          OC {oncostApplied === true ? "ON" : oncostApplied === false ? "OFF" : "—"}
        </button>
      )}
      {(verdict || oncostApplied != null || reason) && (
        <input
          type="text"
          value={reason}
          placeholder="Override reason…"
          onChange={(e) => onChange({ reason: e.target.value })}
          data-testid={`ledger-row-${row.row_id}-reason`}
          className="px-1.5 py-0.5 border border-slate-300 rounded text-[10.5px] w-[160px]"
        />
      )}
    </div>
  );
}


export function LedgerChecker() {
  const [file, setFile] = useState(null);
  const [fy, setFy] = useState("FY24-25");
  const [entityType, setEntityType] = useState("council");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showLimit, setShowLimit] = useState(500);
  const [overrides, setOverrides] = useState({});
  const [selected, setSelected] = useState({});

  const setRowOverride = (rowId, patch) => {
    setOverrides(prev => {
      const cur = { ...(prev[rowId] || {}) };
      Object.assign(cur, patch);
      // Drop the row entry entirely if all fields are clear
      if ((cur.verdict == null || cur.verdict === undefined) &&
          (cur.oncost_applied == null || cur.oncost_applied === undefined) &&
          !cur.reason) {
        const nxt = { ...prev };
        delete nxt[rowId];
        return nxt;
      }
      return { ...prev, [rowId]: cur };
    });
  };
  const clearOverrides = () => { setOverrides({}); setSelected({}); };
  const bulkSet = (patch) => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (ids.length === 0) return;
    setOverrides(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = { ...(next[id] || {}), ...patch }; });
      return next;
    });
  };

  const onUpload = async (f) => {
    if (!f) return;
    setFile(f); setData(null); setErr(null); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("fy", fy);
      fd.append("entity_type", entityType);
      const r = await axios.post(`${API}/tools/ledger-checker`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to parse ledger.");
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("fy", fy);
    fd.append("entity_type", entityType);
    if (Object.keys(overrides).length > 0) fd.append("overrides", JSON.stringify(overrides));
    const r = await axios.post(`${API}/tools/ledger-checker/download`, fd, {
      responseType: "blob",
      headers: { "Content-Type": "multipart/form-data" },
    });
    const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(file.name || "ledger").replace(/\.[^.]+$/, "")}_assessed.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const overridden = useMemo(() => applyLedgerOverrides(data, overrides), [data, overrides]);
  const rows = (overridden?.rows || []).filter(r => filter === "all" || r.eligibility === filter);
  const totals = overridden?.totals || {};
  const counts = overridden?.counts || {};
  const overrideCount = Object.keys(overrides).length;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="ledger-checker-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Ledger Checker</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Upload a council GL/MYOB ledger export (.csv or .xlsx). Each line is classified as
          <span className="font-semibold text-emerald-700"> Eligible</span>,
          <span className="font-semibold text-red-700"> Ineligible</span>, or
          <span className="font-semibold text-amber-700"> Review</span> against the NSWRA DRFA
          Cat B eligibility guidelines. On-costs are auto-applied to labour lines that don&apos;t
          already include them, producing an assessed ledger ready to attach to the payment.
        </p>
      </div>

      <div className="card p-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Ledger file (.csv / .xlsx)</div>
            <input
              type="file"
              accept=".csv,.xlsx,.xlsm,.xls"
              data-testid="ledger-checker-file-input"
              onChange={(e) => onUpload(e.target.files?.[0])}
              className="block w-full text-sm text-slate-600 file:mr-4 file:px-4 file:py-2 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer hover:file:bg-slate-700"
            />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Financial Year</div>
            <select
              value={fy}
              onChange={(e) => setFy(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              data-testid="ledger-checker-fy-select"
            >
              <option value="FY22-23">FY22-23</option>
              <option value="FY23-24">FY23-24</option>
              <option value="FY24-25">FY24-25</option>
              <option value="FY25-26">FY25-26</option>
            </select>
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Entity type</div>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              data-testid="ledger-checker-entity-select"
            >
              <option value="council">Council (opt-in)</option>
              <option value="state_agency">State Agency</option>
            </select>
          </label>
        </div>
        {file && (
          <div className="text-[11.5px] font-mono text-slate-500">Selected: {file.name} · {(file.size/1024).toFixed(1)} KB</div>
        )}
      </div>

      {loading && <div className="card p-5 flex items-center gap-3 text-sm text-slate-600"><Loader2 className="w-4 h-4 animate-spin"/> Parsing ledger…</div>}
      {err && <div className="card p-5 text-sm text-red-600">{err}</div>}

      {data && (
        <div className="space-y-4" data-testid="ledger-checker-results">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-emerald-700">Eligible</div><div className="font-mono font-semibold text-emerald-800">{fmtAUD(totals.eligible)} <span className="text-[10px] text-emerald-600">({counts.eligible})</span></div></div>
            <div className="bg-red-50 border border-red-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-red-700">Ineligible</div><div className="font-mono font-semibold text-red-800">{fmtAUD(totals.ineligible)} <span className="text-[10px] text-red-600">({counts.ineligible})</span></div></div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-amber-700">Review</div><div className="font-mono font-semibold text-amber-800">{fmtAUD(totals.review)} <span className="text-[10px] text-amber-600">({counts.review})</span></div></div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2" title="Submitted oncost lines — stripped from totals. Reapplied by assessor on eligible labour only."><div className="text-[9px] uppercase tracking-wider text-blue-700">Stripped (submitted oncosts)</div><div className="font-mono font-semibold text-blue-800">{fmtAUD(totals.stripped)} <span className="text-[10px] text-blue-600">({counts.stripped})</span></div></div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Assessor on-costs<br/><span className="font-mono text-[9.5px]">{data.oncost_pct_ordinary}% / OT {data.oncost_pct_overtime}%</span></div><div className="font-mono font-semibold text-slate-800">{fmtAUD(totals.oncosts)}</div></div>
            <div className="bg-slate-900 border border-slate-900 rounded p-2 text-white"><div className="text-[9px] uppercase tracking-wider text-white/70">Assessed total</div><div className="font-mono font-semibold">{fmtAUD(totals.assessed)}</div></div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
                Assessed ledger · {rows.length} line{rows.length === 1 ? "" : "s"}
                {overrideCount > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-300">
                    {overrideCount} assessor override{overrideCount === 1 ? "" : "s"} applied
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-[11px]">
                  <option value="all">All</option><option value="eligible">Eligible only</option>
                  <option value="ineligible">Ineligible only</option><option value="review">Review only</option>
                  <option value="stripped">Stripped (submitted oncosts)</option>
                </select>
                {overrideCount > 0 && (
                  <button onClick={clearOverrides} data-testid="ledger-clear-overrides" className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Clear overrides</button>
                )}
                <button onClick={download} data-testid="ledger-checker-download" className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 bg-slate-900 text-white rounded hover:bg-slate-700">Download assessed .xlsx</button>
              </div>
            </div>

            {/* Bulk action bar — only shown when ≥1 row is selected */}
            {selectedCount > 0 && (
              <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-slate-100 border border-slate-300 rounded text-[11px]" data-testid="ledger-bulk-bar">
                <span className="font-bold">{selectedCount} selected</span>
                <span className="text-slate-400">·</span>
                <button onClick={() => bulkSet({ verdict: "ineligible" })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700" data-testid="ledger-bulk-ineligible">Mark Ineligible</button>
                <button onClick={() => bulkSet({ verdict: "eligible" })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700" data-testid="ledger-bulk-eligible">Mark Eligible</button>
                <button onClick={() => bulkSet({ verdict: "review" })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600" data-testid="ledger-bulk-review">Mark Review</button>
                <button onClick={() => bulkSet({ oncost_applied: false })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-slate-700 text-white rounded hover:bg-slate-800" data-testid="ledger-bulk-strip-oncost">Strip on-costs</button>
                <button onClick={() => setSelected({})} className="ml-auto text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-slate-300 rounded hover:bg-white" data-testid="ledger-bulk-clear">Clear selection</button>
              </div>
            )}

            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-[10.5px]">
                <thead className="bg-slate-100"><tr className="text-left text-slate-600">
                  <th className="p-1.5 w-7"><input type="checkbox" data-testid="ledger-select-all"
                    onChange={(e) => {
                      const all = e.target.checked;
                      const nxt = {};
                      if (all) rows.slice(0, showLimit).forEach(r => { if (r.eligibility !== "stripped") nxt[r.row_id] = true; });
                      setSelected(nxt);
                    }}/></th>
                  <th className="p-1.5">Date</th><th className="p-1.5">Account</th><th className="p-1.5">Description</th>
                  <th className="p-1.5 text-right">Amount</th><th className="p-1.5">Category</th>
                  <th className="p-1.5">Eligibility</th><th className="p-1.5 min-w-[220px]">Assessor verdict + on-cost</th>
                  <th className="p-1.5 text-right">On-cost</th><th className="p-1.5 text-right">Assessed</th>
                </tr></thead>
                <tbody>
                  {rows.slice(0, showLimit).map((r, i) => {
                    const tone = r.eligibility === "eligible" ? "bg-emerald-50"
                      : r.eligibility === "ineligible" ? "bg-red-50"
                      : r.eligibility === "stripped" ? "bg-blue-50 line-through opacity-75"
                      : "bg-amber-50";
                    const overriddenBorder = r.was_overridden ? "border-l-2 border-l-amber-500" : "";
                    return (
                      <tr key={r.row_id || i} className={`border-t border-slate-100 ${tone} ${overriddenBorder}`}>
                        <td className="p-1.5">
                          {r.eligibility !== "stripped" && (
                            <input type="checkbox" data-testid={`ledger-select-${r.row_id}`}
                              checked={!!selected[r.row_id]}
                              onChange={(e) => setSelected(s => ({ ...s, [r.row_id]: e.target.checked }))}/>
                          )}
                        </td>
                        <td className="p-1.5 whitespace-nowrap font-mono">{r.date || "—"}</td>
                        <td className="p-1.5 truncate max-w-[140px]" title={r.account}>{r.account || "—"}</td>
                        <td className="p-1.5 truncate max-w-[240px]" title={r.description}>{r.description || "—"}</td>
                        <td className="p-1.5 font-mono text-right">{fmtAUD(r.amount)}</td>
                        <td className="p-1.5 capitalize">{r.category}</td>
                        <td className="p-1.5 capitalize font-semibold" title={r.reason}>
                          {r.eligibility}
                          {r.was_overridden && <span className="ml-1 text-amber-600" title={`Overridden by assessor — original: ${r.tool_eligibility}`}>⚙</span>}
                        </td>
                        <td className="p-1.5">
                          <LedgerRowOverrideControls
                            row={r}
                            onChange={(patch) => setRowOverride(r.row_id, patch)}
                          />
                        </td>
                        <td className="p-1.5 font-mono text-right">{r.oncost_aud ? fmtAUD(r.oncost_aud) : "—"}</td>
                        <td className="p-1.5 font-mono text-right">{fmtAUD(r.assessed_aud)}</td>
                      </tr>
                    );
                  })}
                  {rows.length > showLimit && (
                    <tr><td colSpan={10} className="p-3 text-center bg-slate-50">
                      <button
                        type="button"
                        onClick={() => setShowLimit(l => l + 500)}
                        data-testid="ledger-checker-show-more"
                        className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-100"
                      >Show {Math.min(500, rows.length - showLimit)} more (of {rows.length - showLimit} remaining)</button>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

